import { AppConfig } from '@/config';
import { AppConstants, COOKIE_MAX_AGE } from '@/utils/constants';
import { isTokenExpired } from '@/utils/token';
import { message } from 'antd';
import i18next from 'i18next';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import { jwtDecode } from 'jwt-decode';
import { setAuth0UserId, getAuth0UserId } from '@/services/auth0UserStore';

// Components compare response statuses without importing axios themselves.
export { HttpStatusCode } from 'axios';

let getAuth0Token: (() => Promise<string>) | null = null;

export const setAuth0TokenGetter = (tokenGetter: () => Promise<string>) => {
  getAuth0Token = tokenGetter;
};

class HttpService {
  public readonly axiosInstance: AxiosInstance;
  private token: string = '';
  private refreshingTokenPromise: Promise<string> | null = null;

  constructor(baseURL: string) {
    // Restore SaaS token from localStorage if it was set during URL-based login
    this.token = localStorage.getItem('access_token') ?? '';

    this.axiosInstance = axios.create({
      baseURL,
      timeout: AppConfig.axios.timeout,
    });

    this.axiosInstance.interceptors.request.use(async config => {
      const authorization = await this.authorization();
      if (authorization) {
        config.headers = {
          ...config.headers,
          Authorization: authorization,
        } as AxiosRequestHeaders;
      }

      // The API words its messages in this language rather than the
      // browser's Accept-Language (documents-api.md §1).
      if (i18next.language) {
        // The headers may be a plain object here (Authorization above).
        (config.headers as Record<string, string>)['X-Locale'] =
          i18next.language.startsWith('en') ? 'en' : 'ja';
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        return Promise.reject(error);
      },
    );
  }

  /**
   * The Authorization header value for the next request (also used by the
   * run event stream, which is read with fetch). Picks up a token from the
   * URL (SaaS entry), refreshes an expired Auth0 token; undefined without one.
   */
  public async authorization(): Promise<string | undefined> {
    // Highest priority: token in the URL (SaaS entry point).
    // Read it now, set it in-memory, and strip it from the URL so it doesn't persist.
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token') || urlParams.get('access_token');
    if (urlToken) {
      this.token = urlToken;
      localStorage.setItem('access_token', urlToken);
      urlParams.delete('token');
      urlParams.delete('access_token');
      const cleanUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // Restore from localStorage if the in-memory token was cleared (e.g., page reload)
    const storedSaasToken = localStorage.getItem('access_token');
    if (storedSaasToken && !this.token) {
      this.token = storedSaasToken;
    }

    const storedToken = localStorage.getItem('access_token');
    const isSaasToken = !!storedToken && this.token === storedToken;
    let tokenToUse: string | undefined = this.token;

    if (!tokenToUse && !isSaasToken && getAuth0Token) {
      try {
        tokenToUse = await getAuth0Token();
        if (tokenToUse) {
          this.token = tokenToUse;
        }
      } catch (auth0Error) {
        console.error('Failed to get token from Auth0:', auth0Error);
      }
    }

    // Only run refresh logic for Auth0 tokens — SaaS tokens cannot be refreshed via Auth0.
    if (!isSaasToken && tokenToUse && isTokenExpired(tokenToUse)) {
      if (!this.refreshingTokenPromise) {
        this.refreshingTokenPromise = this.refreshToken()
          .then(newToken => {
            this.token = newToken;
            return newToken;
          })
          .finally(() => {
            this.refreshingTokenPromise = null;
          });
      }

      try {
        tokenToUse = await this.refreshingTokenPromise;
      } catch (error) {
        this.refreshingTokenPromise = null;
        if (getAuth0Token) {
          try {
            tokenToUse = await getAuth0Token();
            if (tokenToUse) {
              this.token = tokenToUse;
            }
          } catch (auth0Error) {
            console.error('Failed to get token from Auth0:', auth0Error);
            tokenToUse = undefined;
          }
        } else {
          tokenToUse = undefined;
        }
      }
    }

    if (tokenToUse) {
      const isSaasToken =
        !!localStorage.getItem('access_token') &&
        tokenToUse === localStorage.getItem('access_token');

      // SaaS tokens are sent as a plain Bearer token (no |uid=).
      // Auth0 tokens append |uid=<auth0_id> so the backend can resolve the user.
      // The sub (Auth0 user ID) is extracted directly from the JWT payload.
      let authorizationValue: string;
      if (isSaasToken) {
        authorizationValue = `Bearer ${tokenToUse}`;
      } else {
        let auth0Sub = getAuth0UserId();
        if (!auth0Sub) {
          try {
            const payload = jwtDecode<{ sub?: string }>(tokenToUse);
            auth0Sub = payload.sub ?? '';
          } catch {
            // fallback: nenhum sub encontrado no token
          }
        }
        authorizationValue = `Bearer ${tokenToUse}|uid=${auth0Sub}`;
      }
      return authorizationValue;
    }
    return undefined;
  }

  private async refreshToken(): Promise<string> {
    if (getAuth0Token) {
      return await getAuth0Token();
    }

    return new Promise((resolve, _reject) => {
      const listener = (event: MessageEvent) => {
        if (
          event.origin === AppConstants.SAAS_DOMAIN &&
          event.data?.type === 'reload' &&
          event.data?.token
        ) {
          window.removeEventListener('message', listener);
          resolve(event.data.token);
        }
      };

      window.addEventListener('message', listener);
      window?.parent?.postMessage(
        { type: AppConstants.reloadMessage },
        AppConstants.SAAS_DOMAIN,
      );
    });
  }

  private waitForTokenFromParent(): Promise<string> {
    return this.refreshToken();
  }

  public updateToken(token: string) {
    this.token = token;
  }

  /** The token the next request starts from (may be empty). */
  public currentToken(): string {
    return this.token || localStorage.getItem('access_token') || '';
  }

  private async request<T>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: any,
    params?: any,
    headers?: any,
  ): Promise<T> {
    try {
      const config: AxiosRequestConfig = { method, url, data, params, headers };
      const response = await this.axiosInstance.request<T>(config);
      return response.data;
    } catch (error: any) {
      const status = error.response?.status;
      const responseData = error.response?.data;
      const asString = (value: unknown): string | undefined =>
        typeof value === 'string' && value.length > 0 ? value : undefined;
      const errorMessage =
        asString(responseData?.detail) ||
        asString(responseData?.message) ||
        asString(responseData?.error) ||
        error.message;
      // Handle 401 Unauthorized - User needs to login again
      if (status === 401) {
        // The session expired: the user's unfinished runs and unsaved
        // drafts are kept for when they sign in again.
        handleLogout('expired');
        return {
          status: 401,
          success: false,
          error: 'Unauthorized. Please login again.',
        } as T;
      }

      // A v2 error with a stable `code` (documents-api.md §1) is a rule the
      // caller explains itself, not an expired session.
      const code = asString(responseData?.code);

      // Handle 403 Forbidden - User is authenticated but doesn't have permission
      // Handle 307 Temporary Redirect - Used for redirecting to login or refresh token flow
      if ([403, 307].includes(status) && !code) {
        if (window.self !== window.top) {
          try {
            window?.parent?.postMessage(
              { type: AppConstants.reloadMessage },
              AppConstants.SAAS_DOMAIN,
            );
          } catch (postError) {
            // Silently fail
          }
        }
        message.error(errorMessage);
      }
      return {
        // 0 when no response arrived (network failure, timeout).
        status: status || (error.response ? 200 : 0),
        success: false,
        error: errorMessage,
        ...(code ? { code } : {}),
        ...(responseData && typeof responseData === 'object'
          ? { body: responseData }
          : {}),
      } as T;
    }
  }

  public get<T>(url: string, params?: any, headers?: any) {
    return this.request<T>('get', url, undefined, params, headers);
  }

  public post<T>(url: string, data?: any, params?: any, headers?: any) {
    return this.request<T>('post', url, data, params, headers);
  }

  public put<T>(url: string, data?: any, params?: any, headers?: any) {
    return this.request<T>('put', url, data, params, headers);
  }

  public delete<T>(url: string, params?: any, data?: any, headers?: any) {
    return this.request<T>('delete', url, data, params, headers);
  }
}

class HttpServices {
  public readonly llmService: HttpService;
  public readonly saasService: HttpService;

  constructor() {
    this.llmService = new HttpService(AppConfig.axios.llm_endpoint);
    this.saasService = new HttpService(AppConfig.axios.saas_endpoint);
  }
}

export const httpServices = new HttpServices();

/**
 * Logout utility function that clears all authentication data and redirects to login
 * Clears: tokens, cookies, localStorage, sessionStorage, and auth0UserId
 */
export const handleLogout = (reason?: 'expired') => {
  httpServices.llmService.updateToken('');
  httpServices.saasService.updateToken('');
  setAuth0UserId(null);
  localStorage.removeItem('access_token');
  // Everything goes except the per-user preference caches, which restore
  // that user's appearance without a flicker at their next sign-in, and,
  // when the session only expired, the user's own unfinished runs (their
  // answers keep coming after signing in again).
  for (const key of Object.keys(localStorage)) {
    const keep =
      key.startsWith(KEEP_ON_SIGN_OUT) ||
      (reason === 'expired' &&
        (key.startsWith(OWN_RUNS_PREFIX) || key.startsWith(DRAFTS_PREFIX)));
    if (!keep) localStorage.removeItem(key);
  }
  sessionStorage.clear();
  window.location.href = `${window.location.origin}/signin${
    reason === 'expired' ? '?reason=expired' : ''
  }`;
};

/** localStorage prefix kept by handleLogout (preferencesStore CACHE_PREFIX). */
export const KEEP_ON_SIGN_OUT = 'qa-assist.preferences.';

/** localStorage prefix of the run store's own runs, kept when a session expires. */
export const OWN_RUNS_PREFIX = 'qa-assist.runs.own.';

/**
 * localStorage prefix of the composer drafts (useDraft), kept when a session
 * expires: the text typed since the last server save survives signing in
 * again. Drafts are keyed by user, so another user never sees them.
 */
export const DRAFTS_PREFIX = 'qa-assist.draft.';
